import {
  PHOTO_LOCAL_LIMIT_BYTES,
  PHOTO_MAX_BYTES,
  REGISTER_NOTE_MAX_TOPICS,
  type PaymentMethod,
  clampRegisterNoteText,
  clampVoidNote,
  isRegisterNoteTopic,
  type RegisterNoteOutcome,
  type RegisterNoteTopic,
  type SnapshotCapabilities,
  type VoidReason,
} from '@barso/shared';
import { MAX_PRICE_CENTS, MIN_PRICE_CENTS, cartTotal } from './cart';
import { manualPayload } from './manualLine';
import { priceChangePayload } from './priceChange';
import { openPosDb, req, transact } from './db';
import { NO_PROMO_STATE, promoIdFor, toPromoViews, type PromoState } from './promo';
import type {
  CartLine,
  CatalogItem,
  InvoiceRequest,
  LocalSale,
  OutboxEntry,
  ParkedCart,
  PendingPhoto,
  RegisterMeta,
  RegisterNoteEntry,
  SalePayload,
  SnapshotPromoJson,
  SyncResponse,
  VoidEntry,
} from './types';

/** Höchstzahl geparkter Körbe (P1 bis P3). */
export const MAX_PARKED = 3;

/** So oft wird ein Storno wiederholt, dessen Verkauf beim Server (noch) nicht bekannt ist, bevor er als fehlgeschlagen gilt. */
const MAX_VOID_ATTEMPTS = 10;

/**
 * Hat der Verkauf eine Zeile mit `promoId` (X7)? Prüft die Form defensiv: Ein beschädigter Eintrag (ohne Zeilen) wird nicht
 * zurückgehalten, sondern wie bisher gesendet.
 */
const hasPromoLine = (entry: OutboxEntry): boolean =>
  Array.isArray(entry.payload.lines) &&
  entry.payload.lines.some((line) => line.promoId !== undefined);

const voidKey = (saleClientUuid: string, lineNo: number | undefined): string =>
  `${saleClientUuid}:${lineNo === undefined ? '*' : String(lineNo)}`;

/**
 * Meta-Schlüssel: höchste Nummer dieses Geräts bei der letzten Übernahme der Kasse (Schichtwechsel). Verkäufe bis dahin gehören
 * der vorigen Person und lassen sich hier nicht stornieren.
 */
export const HANDOVER_FLOOR_KEY = 'handoverFloorSeq';

/**
 * Meta-Schlüssel: Einmal-Code der Freigabe zur Übernahme (nur auf diesem Gerät, nur nach „Übergeben“). Er ist KEINE Sitzung: Mit
 * ihm lässt sich nur die Kasse übernehmen (Person mit Kassenrecht im selben Basar), weder senden noch die Artikelliste laden.
 * Solange er gesetzt ist, gehört die Kasse niemandem: weder die Kassenseite noch der Hintergrund-Sender senden.
 */
export const HANDOVER_KEY = 'handoverToken';
/** Schlüssel im Store `meta` für wartende Kaufbeleg-Anfragen (Welle 5d). */
const INVOICE_KEY = 'invoiceRequests';

/**
 * Lokaler Speicher der Kasse (IndexedDB): Snapshot der Artikel, Warteschlange der Verkäufe (Outbox) und Stornos, Verlauf.
 * Alles, was Geld betrifft, wird in EINER Transaktion gespeichert, bevor die Oberfläche „bezahlt“ zeigt (POS-07).
 */
export class PosStore {
  private constructor(
    private readonly db: IDBDatabase,
    private readonly now: () => Date,
    private readonly newId: () => string,
  ) {}

  static async open(
    name: string,
    options: { now?: () => Date; newId?: () => string } = {},
  ): Promise<PosStore> {
    return new PosStore(
      await openPosDb(name),
      options.now ?? (() => new Date()),
      options.newId ?? (() => crypto.randomUUID()),
    );
  }

  close(): void {
    this.db.close();
  }

  // --- Metadaten -----------------------------------------------------------------------------------------------------

  async getMeta<T>(key: string): Promise<T | undefined> {
    return transact(this.db, ['meta'], 'readonly', async ({ meta }) => {
      return (await req(meta.get(key))) as T | undefined;
    });
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      await req(meta.put(value, key));
    });
  }

  async register(): Promise<RegisterMeta | undefined> {
    return this.getMeta<RegisterMeta>('register');
  }

  /** Legt die Kasse fest; die Zählung beginnt bei 1. Eine bestehende Kasse wird nie überschrieben. */
  async setRegister(meta: RegisterMeta): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta: store }) => {
      const existing = (await req(store.get('register'))) as RegisterMeta | undefined;
      if (existing !== undefined && existing.registerId !== meta.registerId) {
        throw new Error('Diese Kasse ist schon einer anderen Kasse zugeordnet.');
      }
      await req(store.put(meta, 'register'));
      if ((await req(store.get('seq'))) === undefined) await req(store.put(0, 'seq'));
    });
  }

  /** Vermerkt die Person, der die Kasse gehört (Eröffnung, Übernahme, erster Start einer älteren Kasse). Sonst bleibt alles. */
  async setOwner(ownerUserId: string): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      const existing = (await req(meta.get('register'))) as RegisterMeta | undefined;
      if (existing === undefined) return;
      await req(meta.put({ ...existing, ownerUserId }, 'register'));
    });
  }

  /** Speichert (oder entfernt mit `undefined`) die Basar-Sitzung der Kasse (SYN-10). Verkäufe und Kasse bleiben unberührt. */
  async setSession(sessionToken: string | undefined, sessionExpiresAt?: string): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      const existing = (await req(meta.get('register'))) as RegisterMeta | undefined;
      if (existing === undefined) return;
      const next: RegisterMeta = {
        eventId: existing.eventId,
        registerId: existing.registerId,
        label: existing.label,
        labelSalt: existing.labelSalt,
        ...(existing.mode === undefined ? {} : { mode: existing.mode }),
        ...(existing.stationId === undefined ? {} : { stationId: existing.stationId }),
        ...(existing.ownerUserId === undefined ? {} : { ownerUserId: existing.ownerUserId }),
        ...(sessionToken === undefined ? {} : { sessionToken, sessionExpiresAt }),
      };
      await req(meta.put(next, 'register'));
    });
  }

  async updateSalt(labelSalt: number): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      const existing = (await req(meta.get('register'))) as RegisterMeta | undefined;
      if (existing !== undefined) await req(meta.put({ ...existing, labelSalt }, 'register'));
    });
  }

  // --- Rabattaktion (X7) -----------------------------------------------------------------------------------------------
  // Liegt im Store `meta` (keine neue IndexedDB-Version): `promos` und `promosCapable` kommen aus dem Snapshot, `promoActive`
  // ist die von Hand gestartete Aktion dieser Kasse.

  /**
   * Übernimmt die Aktionen aus dem Snapshot. Fehlt das Feld `promos` (älterer Server), merkt sich die Kasse, dass der Server
   * `promoId` nicht kennt: Sie sendet es dann nie (ein älteres Image weist unbekannte Felder mit 400 ab).
   */
  async setPromos(promos: readonly SnapshotPromoJson[] | undefined): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      await req(meta.put(promos !== undefined, 'promosCapable'));
      if (promos !== undefined) await req(meta.put([...promos], 'promos'));
    });
  }

  async promoState(): Promise<PromoState> {
    return transact(this.db, ['meta'], 'readonly', async ({ meta }) => {
      const promos = (await req(meta.get('promos'))) as SnapshotPromoJson[] | undefined;
      const capable = (await req(meta.get('promosCapable'))) === true;
      const activeId = (await req(meta.get('promoActive'))) as string | undefined;
      if (promos === undefined && !capable && activeId === undefined) return NO_PROMO_STATE;
      return {
        promos: toPromoViews(promos ?? []),
        capable,
        activeId: activeId ?? null,
      };
    });
  }

  /** Startet (oder beendet mit `null`) die Aktion an dieser Kasse. Rein lokal, kein Verkauf. */
  async setActivePromo(promoId: string | null): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      if (promoId === null) await req(meta.delete('promoActive'));
      else await req(meta.put(promoId, 'promoActive'));
    });
  }

  // --- Fähigkeiten des Servers (U17) -------------------------------------------------------------------------------------
  // Liegen im Store `meta` (keine neue IndexedDB-Version). Neue Felder gehen nur hinaus, wenn der zuletzt geladene Stand sie
  // meldete: Ein älteres Image weist unbekannte Felder mit 400 ab, und zwar den ganzen Stapel.

  /** Übernimmt `capabilities` aus dem Stand; `undefined` (älterer Server oder Rückfall nach 400) setzt sie zurück. */
  async setCapabilities(capabilities: SnapshotCapabilities | undefined): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      if (capabilities === undefined) await req(meta.delete('capabilities'));
      else await req(meta.put({ ...capabilities }, 'capabilities'));
    });
  }

  async capabilities(): Promise<SnapshotCapabilities> {
    return (await this.getMeta<SnapshotCapabilities>('capabilities')) ?? {};
  }

  /**
   * Rückfall nach 400 (U17g): Entfernt `priceChange` aus den Verkäufen der Warteschlange, die noch nie angenommen wurden (ein
   * 400 nimmt nichts an). Der Preis der Zeile ist schon der korrigierte; der Grund bleibt im Verlauf und im Notausgang. So geht
   * nie ein Verkauf verloren und keiner wird wegen des Feldes abgelehnt. Gibt die Zahl der geänderten Verkäufe zurück.
   */
  async stripPriceChanges(): Promise<number> {
    return transact(this.db, ['outbox'], 'readwrite', async ({ outbox }) => {
      const pending = (await req(outbox.getAll())) as OutboxEntry[];
      let changed = 0;
      for (const entry of pending) {
        if (entry.status !== 'pending' || !Array.isArray(entry.payload.lines)) continue;
        if (!entry.payload.lines.some((line) => line.priceChange !== undefined)) continue;
        await req(
          outbox.put({
            ...entry,
            payload: {
              ...entry.payload,
              lines: entry.payload.lines.map((line) => {
                const copy = { ...line };
                delete copy.priceChange;
                return copy;
              }),
            },
          }),
        );
        changed += 1;
      }
      return changed;
    });
  }

  /**
   * Zahlart „Karte“ (U14) aus den noch nie angenommenen Verkäufen der Outbox nehmen (ein älterer Server weist `card` mit 400
   * ab): Sie gehen dann als bar hinaus, es geht kein Verkauf verloren. Der Vermerk „Karte“ bleibt im Verlauf der Kasse.
   */
  async stripCardPayments(): Promise<number> {
    return transact(this.db, ['outbox', 'history'], 'readwrite', async ({ outbox, history }) => {
      const pending = (await req(outbox.getAll())) as OutboxEntry[];
      let changed = 0;
      for (const entry of pending) {
        if (entry.status !== 'pending' || entry.payload.payment !== 'card') continue;
        await req(outbox.put({ ...entry, payload: { ...entry.payload, payment: 'cash' } }));
        // Im Verlauf vermerken, dass dieser Kartenverkauf als bar übertragen wurde
        const sale = (await req(history.get(entry.clientUuid))) as LocalSale | undefined;
        if (sale !== undefined) await req(history.put({ ...sale, sentAsCash: true }));
        changed += 1;
      }
      return changed;
    });
  }

  // --- Snapshot ------------------------------------------------------------------------------------------------------

  /** Sucht über den BARSO-Code und, wenn keiner passt, über einen fremden Code (übernommenes Etikett). */
  async findByCode(code: string): Promise<CatalogItem | undefined> {
    return transact(this.db, ['items'], 'readonly', async ({ items }) => {
      const byLabel = (await req(items.index('code').get(code))) as CatalogItem | undefined;
      if (byLabel !== undefined) return byLabel;
      return (await req(items.index('altCode').get(code))) as CatalogItem | undefined;
    });
  }

  /**
   * Sucht über Verkäufernummer und Artikelnummer (Eingabe „17/5“, O3). Ein nicht gelöschter Artikel hat Vorrang; nur wenn es
   * keinen gibt, kommt der gelöschte (die Kasse warnt dann wie beim Scan).
   */
  async findBySellerItem(sellerNumber: number, itemNo: number): Promise<CatalogItem | undefined> {
    return transact(this.db, ['items'], 'readonly', async ({ items }) => {
      const hits = (await req(
        items.index('sellerItem').getAll([sellerNumber, itemNo]),
      )) as CatalogItem[];
      return hits.find((item) => !item.deleted) ?? hits[0];
    });
  }

  /** Alle Artikel des lokalen Stands (für den Suchindex der Kasse, U17b; einmal im Leerlauf). */
  async allItems(): Promise<CatalogItem[]> {
    return transact(this.db, ['items'], 'readonly', async ({ items }) => {
      return (await req(items.getAll())) as CatalogItem[];
    });
  }

  /** Ist diese Verkäufernummer im Stand der Kasse bekannt (mindestens ein Artikel mit Artikelnummer)? Nie ein Name. */
  async sellerKnown(sellerNumber: number): Promise<boolean> {
    return transact(this.db, ['items'], 'readonly', async ({ items }) => {
      const range = IDBKeyRange.bound([sellerNumber, 0], [sellerNumber, Number.MAX_SAFE_INTEGER]);
      return (await req(items.index('sellerItem').count(range))) > 0;
    });
  }

  async itemCount(): Promise<number> {
    return transact(this.db, ['items'], 'readonly', async ({ items }) => req(items.count()));
  }

  /**
   * Übernimmt Artikel des Servers (auch Löschvermerke). Was diese Kasse lokal schon verkauft oder storniert hat, aber noch
   * nicht gesendet ist, gilt weiter: Ein verspäteter Stand vom Server macht einen lokal verkauften Artikel nicht wieder
   * verkäuflich. Gibt die Artikel so zurück, wie sie gespeichert wurden (für den Suchindex der Kasse, U17b).
   */
  async applySnapshot(incoming: readonly CatalogItem[]): Promise<CatalogItem[]> {
    return transact(
      this.db,
      ['items', 'history', 'voids'],
      'readwrite',
      async ({ items, history, voids }) => {
        const held = new Map<string, 'sold' | 'received'>();
        const sales = (await req(history.getAll())) as LocalSale[];
        const pendingVoids = (await req(voids.getAll())) as VoidEntry[];
        const voidedSales = new Set(pendingVoids.map((entry) => entry.saleClientUuid));
        for (const sale of sales) {
          if (sale.state !== 'pending' && !voidedSales.has(sale.clientUuid)) continue;
          for (const line of sale.lines) {
            held.set(line.code, sale.voided || line.voided ? 'received' : 'sold');
          }
        }
        const written: CatalogItem[] = [];
        for (const item of incoming) {
          // Offline als unbekannt verkaufte Zeile mit fremdem Code: zählt ebenfalls
          const override =
            held.get(item.code) ??
            (item.altCode === undefined || item.altCode === null
              ? undefined
              : held.get(item.altCode));
          const stored =
            override === undefined || item.deleted ? item : { ...item, status: override };
          await req(items.put(stored));
          written.push(stored);
        }
        return written;
      },
    );
  }

  // --- Verkauf -------------------------------------------------------------------------------------------------------

  /**
   * Speichert einen Verkauf fest: Nummer vergeben, Ereignis in die Outbox, Verlauf, Artikel lokal „verkauft“, alles in einer
   * Transaktion. Erst wenn diese Methode zurückkehrt, darf die Oberfläche „bezahlt“ zeigen.
   */
  async checkout(
    cart: readonly CartLine[],
    givenCents: number | null,
    payment: PaymentMethod = 'cash',
  ): Promise<LocalSale> {
    if (cart.length === 0) throw new Error('Der Warenkorb ist leer.');
    // Karte ist nur eine Markierung: Es gibt kein „gegeben“ und kein Rückgeld
    if (payment === 'card' && givenCents !== null) {
      throw new Error('Bei Kartenzahlung gibt es keinen gegebenen Betrag.');
    }
    if (
      new Set(cart.map((line) => line.code)).size !== cart.length ||
      new Set(cart.filter((line) => line.itemId !== null).map((line) => line.itemId)).size !==
        cart.filter((line) => line.itemId !== null).length
    ) {
      throw new Error('Ein Artikel liegt doppelt im Warenkorb.');
    }
    for (const line of cart) {
      // Eine Preiskorrektur (U17g) darf bis auf 0,00 € gehen, sonst gilt der Mindestpreis der Kasse
      const minCents = line.priceChange === undefined ? MIN_PRICE_CENTS : 0;
      if (
        !Number.isInteger(line.priceCents) ||
        line.priceCents < minCents ||
        line.priceCents > MAX_PRICE_CENTS
      ) {
        throw new Error('Ein Preis liegt außerhalb des zulässigen Bereichs.');
      }
    }
    const totalCents = cartTotal(cart);
    if (givenCents !== null && givenCents < totalCents) {
      throw new Error('Das gegebene Geld reicht nicht.');
    }
    const soldAt = this.now();
    const clientUuid = this.newId();
    return transact(
      this.db,
      ['meta', 'items', 'outbox', 'history', 'photos'],
      'readwrite',
      async ({ meta, items, outbox, history, photos }) => {
        const registerSeq = (((await req(meta.get('seq'))) as number | undefined) ?? 0) + 1;
        await req(meta.put(registerSeq, 'seq'));
        // `promoId` nur, wenn der zuletzt geladene Snapshot `promos` zeigte (der Server kennt es, X7)
        const capable = (await req(meta.get('promosCapable'))) === true;
        // Preiskorrektur (U17g): nur mit gemeldeter Fähigkeit; sonst geht die Zeile mit dem korrigierten Preis hinaus
        const capabilities = ((await req(meta.get('capabilities'))) ?? {}) as SnapshotCapabilities;
        const payload: SalePayload = {
          clientUuid,
          registerSeq,
          soldAt: soldAt.toISOString(),
          payment,
          totalCents,
          lines: cart.map((line) => {
            const promoId = promoIdFor(line, capable);
            const priceChange = priceChangePayload(line, capabilities.priceChangeReasons);
            // Artikel ohne Etikett (U17h): nur mit gemeldeter Fähigkeit, sonst geht die Zeile als unbekannter Code mit Preis
            const manual = manualPayload(line, capabilities.manualLines === true);
            return {
              code: line.code,
              priceCents: line.priceCents,
              ...(promoId === undefined ? {} : { promoId }),
              ...(priceChange === undefined ? {} : { priceChange }),
              ...(manual === undefined ? {} : { manual }),
            };
          }),
        };
        const entry: OutboxEntry = {
          clientUuid,
          registerSeq,
          payload,
          status: 'pending',
          attempts: 0,
        };
        await req(outbox.put(entry));
        const sale: LocalSale = {
          clientUuid,
          registerSeq,
          soldAt: payload.soldAt,
          soldAtMs: soldAt.getTime(),
          totalCents,
          givenCents,
          // Nur Karte wird vermerkt; bar bleibt wie vor U14 ohne Feld
          ...(payment === 'card' ? { payment } : {}),
          lines: cart.map((line) => ({ ...line, voided: false })),
          voided: false,
          state: 'pending',
          conflicts: [],
          priceMismatches: [],
        };
        await req(history.put(sale));
        // Fotos der manuellen Zeilen gehören ab jetzt zu diesem Verkauf (hochgeladen wird erst nach seiner Bestätigung)
        for (const line of cart) {
          const photoId = line.manual?.photoId;
          if (photoId === undefined) continue;
          const photo = (await req(photos.get(photoId))) as PendingPhoto | undefined;
          if (photo !== undefined) await req(photos.put({ ...photo, saleClientUuid: clientUuid }));
        }
        for (const line of cart) {
          if (line.itemId === null) continue;
          const item = (await req(items.get(line.itemId))) as CatalogItem | undefined;
          if (item !== undefined) await req(items.put({ ...item, status: 'sold' }));
        }
        return sale;
      },
    );
  }

  /**
   * Storno eines ganzen Verkaufs oder einer Zeile (POS-06). Das Storno ist selbst ein Ereignis in der Warteschlange; die
   * Artikel sind lokal sofort wieder verkäuflich.
   */
  async voidSale(
    clientUuid: string,
    lineNo?: number,
    why: { reason?: VoidReason | undefined; note?: string | undefined } = {},
  ): Promise<LocalSale> {
    // Der Text wird nur hier gekürzt (eine Stelle, U17d)
    const note = clampVoidNote(why.note);
    const reason = why.reason;
    return transact(
      this.db,
      ['meta', 'items', 'voids', 'history'],
      'readwrite',
      async ({ meta, items, voids, history }) => {
        const sale = (await req(history.get(clientUuid))) as LocalSale | undefined;
        if (sale === undefined) throw new Error('Diesen Verkauf gibt es auf dieser Kasse nicht.');
        if (lineNo !== undefined && (lineNo < 1 || lineNo > sale.lines.length)) {
          throw new Error('Diese Position gibt es nicht.');
        }
        const targets = sale.lines
          .map((line, index) => ({ line, index }))
          .filter(({ index }) => lineNo === undefined || index + 1 === lineNo);
        if (targets.every(({ line }) => line.voided)) throw new Error('Schon storniert.');
        // Nach „Kasse übergeben“ gehören frühere Verkäufe der vorigen Person: Der Server lehnt ihren Storno ab (nur die Leitung
        // darf fremde Verkäufe stornieren). Hier schon ablehnen, sonst wäre der Artikel lokal frei und das Geld ausgezahlt.
        const floor: unknown = await req(meta.get(HANDOVER_FLOOR_KEY));
        if (typeof floor === 'number' && sale.registerSeq <= floor) {
          throw new Error(
            'Dieser Verkauf ist vor der Übergabe der Kasse entstanden. Stornieren kann ihn nur die Leitung des Basars.',
          );
        }
        const updated: LocalSale = {
          ...sale,
          lines: sale.lines.map((line, index) =>
            lineNo === undefined || index + 1 === lineNo
              ? {
                  ...line,
                  voided: true,
                  ...(reason === undefined ? {} : { voidReason: reason }),
                  ...(note === undefined ? {} : { voidNote: note }),
                }
              : line,
          ),
        };
        updated.voided = updated.lines.every((line) => line.voided);
        await req(history.put(updated));
        const entry: VoidEntry = {
          key: voidKey(clientUuid, lineNo),
          saleClientUuid: clientUuid,
          lineNo,
          afterSeq: ((await req(meta.get('seq'))) as number | undefined) ?? 0,
          ...(reason === undefined ? {} : { reason }),
          ...(note === undefined ? {} : { note }),
          status: 'pending',
          attempts: 0,
        };
        await req(voids.put(entry));
        for (const { line } of targets) {
          if (line.voided || line.itemId === null) continue;
          const item = (await req(items.get(line.itemId))) as CatalogItem | undefined;
          if (item?.status === 'sold') await req(items.put({ ...item, status: 'received' }));
        }
        return updated;
      },
    );
  }

  // --- Fotos zu Artikeln ohne Etikett (U17h) ---------------------------------------------------------------------------------
  // Eigener Store `photos` (DB_VERSION 4). Ein Foto ist kein Geld: Der Verkauf geht auch ohne Foto durch, wird ein Foto nicht
  // gespeichert, sagt die Oberfläche es. Hochgeladen wird erst nach der Bestätigung des Verkaufs.

  /**
   * Legt ein (schon verkleinertes) Foto in die Warteschlange. Zu groß für den Server (über 400 KB) oder kein Platz mehr (30 MB
   * noch nicht hochgeladene Fotos): wirft mit „Foto nicht gespeichert, Verkauf geht trotzdem“.
   */
  async addPhoto(photoId: string, data: ArrayBuffer): Promise<PendingPhoto> {
    const full = new Error('Foto nicht gespeichert, Verkauf geht trotzdem.');
    if (data.byteLength === 0 || data.byteLength > PHOTO_MAX_BYTES) throw full;
    const entry: PendingPhoto = {
      photoId,
      data,
      bytes: data.byteLength,
      createdAtMs: this.now().getTime(),
    };
    await transact(this.db, ['photos'], 'readwrite', async ({ photos }) => {
      const all = (await req(photos.getAll())) as PendingPhoto[];
      const used = all.reduce((sum, photo) => sum + photo.bytes, 0);
      if (used + entry.bytes > PHOTO_LOCAL_LIMIT_BYTES) throw full;
      await req(photos.put(entry));
    });
    return entry;
  }

  async photos(): Promise<PendingPhoto[]> {
    return transact(this.db, ['photos'], 'readonly', async ({ photos }) => {
      return (await req(photos.getAll())) as PendingPhoto[];
    });
  }

  async removePhoto(photoId: string): Promise<void> {
    await transact(this.db, ['photos'], 'readwrite', async ({ photos }) => {
      await req(photos.delete(photoId));
    });
  }

  /** Fotos, die nie zu einem Verkauf wurden (Zeile wieder entfernt), räumt die Kasse nach `maxAgeMs` auf. */
  async purgeUnusedPhotos(maxAgeMs: number): Promise<number> {
    const cutoff = this.now().getTime() - maxAgeMs;
    return transact(this.db, ['photos'], 'readwrite', async ({ photos }) => {
      const all = (await req(photos.getAll())) as PendingPhoto[];
      let removed = 0;
      for (const photo of all) {
        if (photo.saleClientUuid === undefined && photo.createdAtMs < cutoff) {
          await req(photos.delete(photo.photoId));
          removed += 1;
        }
      }
      return removed;
    });
  }

  /** Hat der Server diesen Verkauf bestätigt (angenommen oder schon bekannt)? Erst dann gehen seine Fotos hinaus. */
  async saleConfirmed(clientUuid: string): Promise<boolean> {
    return (await this.saleState(clientUuid)) === 'synced';
  }

  /** Übertragungsstand eines Verkaufs (`undefined`: nicht im Verlauf). */
  async saleState(clientUuid: string): Promise<LocalSale['state'] | undefined> {
    return transact(this.db, ['history'], 'readonly', async ({ history }) => {
      const sale = (await req(history.get(clientUuid))) as LocalSale | undefined;
      return sale?.state;
    });
  }

  /**
   * Rückfall nach 400 (U17h): Entfernt `manual` aus den Verkäufen der Warteschlange, die noch nie angenommen wurden. Die Zeile
   * geht dann wie ein unbekannter Code mit Preis hinaus; Verkäufernummer, Text und Foto bleiben auf dem Gerät (Verlauf,
   * Notausgang). Gibt die Zahl der geänderten Verkäufe zurück.
   */
  async stripManualLines(): Promise<number> {
    return transact(this.db, ['outbox'], 'readwrite', async ({ outbox }) => {
      const pending = (await req(outbox.getAll())) as OutboxEntry[];
      let changed = 0;
      for (const entry of pending) {
        if (entry.status !== 'pending' || !Array.isArray(entry.payload.lines)) continue;
        if (!entry.payload.lines.some((line) => line.manual !== undefined)) continue;
        await req(
          outbox.put({
            ...entry,
            payload: {
              ...entry.payload,
              lines: entry.payload.lines.map((line) => {
                const copy = { ...line };
                delete copy.manual;
                return copy;
              }),
            },
          }),
        );
        changed += 1;
      }
      return changed;
    });
  }

  // --- Kommentare der Kasse (U17f) -----------------------------------------------------------------------------------------
  // Eigener Store `notes` (DB_VERSION 4). Ein Kommentar ist kein Geld: Er geht über den Sync mit, aber nie vor Verkäufen und
  // Stornos, und ein Fehler beim Senden hält keinen Verkauf auf.

  /**
   * Speichert einen Kommentar (zu einem Verkauf bzw. dessen Position, oder ohne Verkauf: „Problem melden“). Er wird erst beim
   * nächsten Abgleich gesendet, auch ohne Netz gespeichert. Der Text wird auf `REGISTER_NOTE_MAX` Zeichen gekürzt (einzige
   * Stelle). Ohne Thema und ohne Text gibt es nichts zu melden.
   */
  async addNote(input: {
    saleClientUuid?: string | undefined;
    lineNo?: number | undefined;
    topics: readonly RegisterNoteTopic[];
    text?: string | undefined;
  }): Promise<RegisterNoteEntry> {
    const topics = [...new Set(input.topics)].filter(isRegisterNoteTopic);
    if (topics.length > REGISTER_NOTE_MAX_TOPICS) {
      throw new Error(`Bitte höchstens ${String(REGISTER_NOTE_MAX_TOPICS)} Themen wählen.`);
    }
    const text = clampRegisterNoteText(input.text);
    if (topics.length === 0 && text === undefined) {
      throw new Error('Bitte ein Thema wählen oder etwas schreiben.');
    }
    const entry: RegisterNoteEntry = {
      clientUuid: this.newId(),
      ...(input.saleClientUuid === undefined ? {} : { saleClientUuid: input.saleClientUuid }),
      ...(input.saleClientUuid === undefined || input.lineNo === undefined
        ? {}
        : { lineNo: input.lineNo }),
      topics,
      ...(text === undefined ? {} : { text }),
      createdAt: this.now().toISOString(),
      status: 'pending',
      attempts: 0,
    };
    await transact(this.db, ['notes'], 'readwrite', async ({ notes }) => {
      await req(notes.put(entry));
    });
    return entry;
  }

  async notes(): Promise<RegisterNoteEntry[]> {
    return transact(this.db, ['notes'], 'readonly', async ({ notes }) => {
      const all = (await req(notes.getAll())) as RegisterNoteEntry[];
      return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    });
  }

  /** Noch nicht bestätigte Kommentare (ohne die als nicht zustellbar markierten), älteste zuerst. */
  async noteBatch(limit: number): Promise<RegisterNoteEntry[]> {
    return (await this.notes())
      .filter((entry) => entry.status === 'pending' && entry.sentAtMs === undefined)
      .slice(0, limit);
  }

  /** Antwort des Servers zu Kommentaren: bestätigt bleibt zur Anzeige, `unknown_sale` wird wiederholt, `rejected` ist endgültig. */
  async applyNoteResults(outcomes: readonly RegisterNoteOutcome[]): Promise<void> {
    const now = this.now().getTime();
    await transact(this.db, ['notes'], 'readwrite', async ({ notes }) => {
      for (const outcome of outcomes) {
        const entry = (await req(notes.get(outcome.clientUuid))) as RegisterNoteEntry | undefined;
        if (entry === undefined) continue;
        if (outcome.status === 'saved' || outcome.status === 'duplicate') {
          await req(notes.put({ ...entry, sentAtMs: now }));
        } else if (outcome.status === 'unknown_sale') {
          const attempts = entry.attempts + 1;
          await req(
            notes.put({
              ...entry,
              attempts,
              status: attempts >= MAX_VOID_ATTEMPTS ? 'failed' : 'pending',
            }),
          );
        } else {
          await req(notes.put({ ...entry, status: 'failed' }));
        }
      }
    });
  }

  // --- Geparkte Körbe (X4) ---------------------------------------------------------------------------------------------
  // Rein lokal: kein Verkauf, keine Nummer (`seq`), keine Outbox, kein Sync, nicht im Notausgang. Es geht kein Geld verloren.

  async parkedList(): Promise<ParkedCart[]> {
    return transact(this.db, ['parked'], 'readonly', async ({ parked }) => {
      const all = (await req(parked.getAll())) as ParkedCart[];
      return all.sort((a, b) => a.parkNo - b.parkNo);
    });
  }

  /** Parkt einen nicht leeren Warenkorb unter dem kleinsten freien Platz (P1 bis P3); sind alle belegt, wird abgewiesen. */
  async park(lines: readonly CartLine[]): Promise<ParkedCart> {
    if (lines.length === 0) throw new Error('Ein leerer Warenkorb lässt sich nicht parken.');
    const createdAtMs = this.now().getTime();
    return transact(this.db, ['parked'], 'readwrite', async ({ parked }) => {
      const used = new Set(((await req(parked.getAllKeys())) as number[]).map(Number));
      let parkNo = 1;
      while (used.has(parkNo)) parkNo += 1;
      if (parkNo > MAX_PARKED) {
        throw new Error(`Es sind schon ${String(MAX_PARKED)} Körbe geparkt.`);
      }
      const cart: ParkedCart = { parkNo, lines: [...lines], createdAtMs };
      await req(parked.put(cart));
      return cart;
    });
  }

  /**
   * Holt einen geparkten Korb zurück und entfernt ihn. Liegt gerade ein Warenkorb an, tritt er in einem Schritt (eine
   * Transaktion) an dessen Platz: Es geht weder der geholte noch der aktuelle Korb verloren.
   */
  async unpark(parkNo: number, current: readonly CartLine[] = []): Promise<ParkedCart> {
    const now = this.now().getTime();
    return transact(this.db, ['parked'], 'readwrite', async ({ parked }) => {
      const found = (await req(parked.get(parkNo))) as ParkedCart | undefined;
      if (found === undefined) throw new Error('Diesen geparkten Korb gibt es nicht mehr.');
      if (current.length > 0) {
        await req(parked.put({ parkNo, lines: [...current], createdAtMs: now }));
      } else {
        await req(parked.delete(parkNo));
      }
      return found;
    });
  }

  /** Verwirft alle geparkten Körbe (Kasse verlassen). Betrifft kein Geld. */
  async discardParked(): Promise<void> {
    await transact(this.db, ['parked'], 'readwrite', async ({ parked }) => {
      await req(parked.clear());
    });
  }

  // --- Warteschlange und Verlauf -------------------------------------------------------------------------------------

  /** Wartende Kommentare (unbestätigt) und Fotos eines Verkaufs (U17f/U17h): kein Geld, aber Anlass zum Senden. */
  async pendingSideWork(): Promise<number> {
    return transact(this.db, ['notes', 'photos'], 'readonly', async ({ notes, photos }) => {
      const open = ((await req(notes.getAll())) as RegisterNoteEntry[]).filter(
        (entry) => entry.status === 'pending' && entry.sentAtMs === undefined,
      ).length;
      const waiting = ((await req(photos.getAll())) as PendingPhoto[]).filter(
        (photo) => photo.saleClientUuid !== undefined,
      ).length;
      return open + waiting;
    });
  }

  async pending(): Promise<{ sales: number; voids: number; failed: number }> {
    return transact(this.db, ['outbox', 'voids'], 'readonly', async ({ outbox, voids }) => {
      const sales = (await req(outbox.getAll())) as OutboxEntry[];
      const stornos = (await req(voids.getAll())) as VoidEntry[];
      return {
        sales: sales.filter((entry) => entry.status === 'pending').length,
        voids: stornos.filter((entry) => entry.status === 'pending').length,
        failed:
          sales.filter((entry) => entry.status === 'failed').length +
          stornos.filter((entry) => entry.status === 'failed').length,
      };
    });
  }

  /**
   * Stornos, die der Server noch nicht bestätigt hat, je Verkauf (Sendestatus der Karte, U17e). `failed` geht vor
   * `pending`: Ein abgelehntes Storno bleibt sichtbar, auch wenn ein weiteres noch wartet.
   */
  async openVoids(): Promise<Map<string, 'pending' | 'failed'>> {
    return transact(this.db, ['voids'], 'readonly', async ({ voids }) => {
      const open = new Map<string, 'pending' | 'failed'>();
      for (const entry of (await req(voids.getAll())) as VoidEntry[]) {
        if (open.get(entry.saleClientUuid) !== 'failed') {
          open.set(entry.saleClientUuid, entry.status);
        }
      }
      return open;
    });
  }

  /**
   * Nächster Stapel: Verkäufe nach Nummer, dazu die Stornos. Ein Storno geht erst mit, wenn alle Verkäufe VOR ihm (bis
   * `afterSeq`) gesendet sind: Sonst könnte der Server es vor einem noch nicht gesendeten Verkauf desselben Artikels
   * verarbeiten. Nichts wird entfernt, bevor der Server bestätigt hat.
   */
  async batch(limit: number): Promise<{ sales: OutboxEntry[]; voids: VoidEntry[] }> {
    return transact(
      this.db,
      ['meta', 'outbox', 'voids'],
      'readonly',
      async ({ meta, outbox, voids }) => {
        const pending = ((await req(outbox.index('seq').getAll())) as OutboxEntry[]).filter(
          (entry) => entry.status === 'pending',
        );
        // Verkäufe mit `promoId` gehen nur an einen Server, der Aktionen kennt (X7): Ein älteres Image weist sie mit 400 ab und
        // würde den ganzen Stapel blockieren. Sie bleiben in der Warteschlange (nie gelöscht, Notausgang) und gehen durch, sobald
        // ein Snapshot wieder `promos` zeigt.
        const capable = (await req(meta.get('promosCapable'))) === true;
        const sendable = capable ? pending : pending.filter((entry) => !hasPromoLine(entry));
        const sales = sendable.slice(0, limit);
        // Frühester noch nicht gesendeter Verkauf (hinter dem Stapel oder zurückgehalten): Stornos davor dürfen mit
        const sent = new Set(sales.map((entry) => entry.clientUuid));
        const nextUnsent =
          pending.find((entry) => !sent.has(entry.clientUuid))?.registerSeq ??
          Number.POSITIVE_INFINITY;
        const stornos = ((await req(voids.getAll())) as VoidEntry[])
          .filter((entry) => entry.status === 'pending' && entry.afterSeq < nextUnsent)
          .sort((a, b) => a.afterSeq - b.afterSeq)
          .slice(0, limit);
        return { sales, voids: stornos };
      },
    );
  }

  /** Zählerstand für den Kassen-Abgleich: höchste vergebene Nummer und Anzahl aller Verkäufe dieser Kasse. */
  async counters(): Promise<{ lastSeq: number; count: number }> {
    return transact(this.db, ['meta', 'history'], 'readonly', async ({ meta, history }) => ({
      lastSeq: ((await req(meta.get('seq'))) as number | undefined) ?? 0,
      count: await req(history.count()),
    }));
  }

  async history(limit = 50): Promise<LocalSale[]> {
    return transact(this.db, ['history'], 'readonly', async ({ history }) => {
      const all = (await req(history.getAll())) as LocalSale[];
      return all.sort((a, b) => b.registerSeq - a.registerSeq).slice(0, limit);
    });
  }

  /** Antwort des Servers einarbeiten: Bestätigtes verlässt die Outbox, Abgelehntes bleibt sichtbar, Unbekanntes wird wiederholt. */
  async applySyncResult(response: SyncResponse): Promise<void> {
    await transact(
      this.db,
      ['outbox', 'voids', 'history'],
      'readwrite',
      async ({ outbox, voids, history }) => {
        for (const outcome of response.sales) {
          const entry = (await req(outbox.get(outcome.clientUuid))) as OutboxEntry | undefined;
          const sale = (await req(history.get(outcome.clientUuid))) as LocalSale | undefined;
          if (entry === undefined) continue;
          if (outcome.status === 'rejected') {
            const failure = {
              code: outcome.code ?? 'REJECTED',
              message: outcome.message ?? 'Abgelehnt.',
            };
            await req(outbox.put({ ...entry, status: 'failed', failure }));
            if (sale !== undefined) await req(history.put({ ...sale, state: 'failed', failure }));
            continue;
          }
          await req(outbox.delete(outcome.clientUuid));
          if (sale !== undefined) {
            await req(
              history.put({
                ...sale,
                state: 'synced',
                syncedAtMs: this.now().getTime(),
                conflicts: outcome.lines.flatMap((line) =>
                  line.result === 'conflict' && line.reason !== undefined
                    ? [{ lineNo: line.lineNo, reason: line.reason }]
                    : [],
                ),
                priceMismatches: outcome.lines
                  .filter((line) => line.priceMismatch)
                  .map((line) => line.lineNo),
                ...(outcome.lines.some((line) => line.promoInvalid === true)
                  ? {
                      promoInvalid: outcome.lines
                        .filter((line) => line.promoInvalid === true)
                        .map((line) => line.lineNo),
                    }
                  : {}),
                note: outcome.note,
              }),
            );
          }
        }
        for (const outcome of response.voids) {
          const key = voidKey(outcome.saleClientUuid, outcome.lineNo);
          const entry = (await req(voids.get(key))) as VoidEntry | undefined;
          if (entry === undefined) continue;
          if (outcome.status === 'voided' || outcome.status === 'already_voided') {
            await req(voids.delete(key));
          } else if (outcome.status === 'unknown_sale') {
            const attempts = entry.attempts + 1;
            await req(
              voids.put({
                ...entry,
                attempts,
                status: attempts >= MAX_VOID_ATTEMPTS ? 'failed' : 'pending',
              }),
            );
          } else {
            await req(voids.put({ ...entry, status: 'failed' }));
          }
        }
      },
    );
  }

  // --- Kaufbeleg per E-Mail (Welle 5d) ---------------------------------------------------------------------------------
  // Liegt im Store `meta` (keine neue IndexedDB-Version) und NICHT im Notausgang: Die Adresse der Kundschaft bleibt nur bis
  // zum Versand auf dem Gerät.

  async invoiceRequests(): Promise<InvoiceRequest[]> {
    return (await this.getMeta<InvoiceRequest[]>(INVOICE_KEY)) ?? [];
  }

  /** Merkt die Anfrage vor (ersetzt eine frühere zum selben Verkauf, z. B. nach einem Tippfehler). */
  async queueInvoice(request: InvoiceRequest): Promise<void> {
    await this.changeInvoices((list) => [
      ...list.filter((entry) => entry.saleClientUuid !== request.saleClientUuid),
      request,
    ]);
  }

  /** Ändert (`request`) oder löscht (`null`) die Anfrage zu einem Verkauf; die Adresse verschwindet damit vom Gerät. */
  async settleInvoice(saleClientUuid: string, request: InvoiceRequest | null): Promise<void> {
    await this.changeInvoices((list) =>
      list.flatMap((entry) =>
        entry.saleClientUuid === saleClientUuid ? (request === null ? [] : [request]) : [entry],
      ),
    );
  }

  /** Löscht wartende Kaufbelege samt Adresse: alle, oder nur die vor `cutoffMs` angelegten. */
  async purgeInvoices(cutoffMs?: number): Promise<void> {
    await this.changeInvoices((list) =>
      cutoffMs === undefined ? [] : list.filter((entry) => entry.createdAtMs >= cutoffMs),
    );
  }

  private async changeInvoices(
    change: (list: InvoiceRequest[]) => InvoiceRequest[],
  ): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      const list = ((await req(meta.get(INVOICE_KEY))) as InvoiceRequest[] | undefined) ?? [];
      const next = change(list);
      if (next.length === 0) await req(meta.delete(INVOICE_KEY));
      else await req(meta.put(next, INVOICE_KEY));
    });
  }

  /** Notausgang (POS-07): alles Ungesendete als Text, falls der Browser die Daten verliert oder der Sync nicht gelingt. */
  async exportAll(): Promise<string> {
    return transact(
      this.db,
      ['meta', 'outbox', 'voids', 'history', 'notes', 'photos'],
      'readonly',
      async ({ meta, outbox, voids, history, notes, photos }) => {
        const snapshot: Record<string, unknown> = {
          exportedAt: this.now().toISOString(),
          register: (await req(meta.get('register'))) as unknown,
          outbox: (await req(outbox.getAll())) as unknown,
          voids: (await req(voids.getAll())) as unknown,
          history: (await req(history.getAll())) as unknown,
          notes: (await req(notes.getAll())) as unknown,
          // Fotos nur als Hinweis (Kennung, Größe, Verkauf), nie die Bilddaten (U17h)
          photos: ((await req(photos.getAll())) as PendingPhoto[]).map((photo) => ({
            photoId: photo.photoId,
            saleClientUuid: photo.saleClientUuid ?? null,
            bytes: photo.bytes,
            hint: 'Foto nicht im Notausgang, es liegt nur auf diesem Gerät',
          })),
        };
        return JSON.stringify(snapshot, null, 2);
      },
    );
  }
}
